import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Funnel } from 'src/api/generated';
import type { ComponentSize } from 'src/modules/shared/types/component.type';
import MenuDropdown from './dropdowns/menu-dropdown';
import type { DropdownItem } from './dropdowns/dropdown';
import { PiGearSix, PiFunnel } from 'react-icons/pi';
import { Text } from '../atoms/text';
import Tooltip from '../atoms/tooltips/tooltip';
import { Route as SettingsFunnelsRoute } from 'src/routes/settings/funnels';
import { settingsSearchSchema } from 'src/modules/settings/schemas/settings.schema';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { FunnelPermission } from 'src/modules/shared/types/permissions.type';
import {
  getMenuItemContentClasses,
  getMenuItemContentColor,
  getMenuItemContentWeight,
} from 'src/modules/shared/styles/dropdown.styles';
import { ButtonSkeleton } from '../atoms/buttons/button-skeleton';
import { classes } from 'src/modules/shared/utils/styles.util';
import { getControlTextSize } from 'src/modules/shared/components/atoms/text/text.styles';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface FunnelSelectorProps {
  funnels: Funnel[];
  selectedFunnelId?: number;
  onFunnelSelect: (id: number) => void;
  size?: ComponentSize;
  className?: string;
  showSettings?: boolean;
  handlerContainerClassName?: string;
  loading?: boolean;
  tooltipContent?: string | ((selectedFunnel: Funnel) => string);
}

/**
 * FunnelSelector component that displays available funnels in a dropdown.
 * Optionally includes a settings link to manage funnels if the user has manage permissions.
 */
export const FunnelSelector: React.FC<FunnelSelectorProps> = ({
  funnels,
  selectedFunnelId,
  onFunnelSelect,
  size = 'md',
  className = '',
  showSettings = false,
  handlerContainerClassName = '',
  loading = false,
  tooltipContent = '',
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Check if user has permissions to manage funnels
  const { hasPermission: canManageFunnels } = useHasPermissions([
    FunnelPermission.Create,
    FunnelPermission.Update,
    FunnelPermission.Delete,
  ]);

  useEffect(() => {
    if (funnels.length > 0 && !selectedFunnelId) {
      onFunnelSelect(funnels[0].id);
    }
  }, [funnels, onFunnelSelect, selectedFunnelId]);

  /**
   * Navigate to the funnels settings page
   */
  const handleOpenSettings = useCallback(() => {
    void navigate({
      to: SettingsFunnelsRoute.to,
      search: (_prev) => {
        return settingsSearchSchema.parse({
          phoneVersion: 'v1',
        });
      },
    });
  }, [navigate]);

  /**
   * Build dropdown items for funnel selector with optional settings link
   */
  const dropdownItems: DropdownItem[] = useMemo(() => {
    const items: DropdownItem[] = funnels.map((funnel) => ({
      content: funnel.name,
      onClick: () => onFunnelSelect(funnel.id),
      selected: selectedFunnelId === funnel.id,
    }));

    // Add separator and settings link if enabled and user has permissions
    if (showSettings && canManageFunnels && items.length > 0) {
      items.push({
        separator: true,
        content: null,
        onClick: () => undefined,
      });

      items.push({
        content: (
          <div className="flex items-center gap-2">
            <PiGearSix className="w-4 h-4" />
            <Text
              size={getControlTextSize('lg')}
              weight={getMenuItemContentWeight(false)}
              color={getMenuItemContentColor(false)}
              className={getMenuItemContentClasses()}
            >
              {/* TODO: check when implementing i18n */}
              Gestionar embudos
            </Text>
          </div>
        ),
        onClick: handleOpenSettings,
      });
    }

    return items;
  }, [
    canManageFunnels,
    funnels,
    handleOpenSettings,
    onFunnelSelect,
    selectedFunnelId,
    showSettings,
  ]);

  const selectedFunnel = useMemo(
    () => funnels.find((funnel) => funnel.id === selectedFunnelId),
    [funnels, selectedFunnelId]
  );

  const tooltipContentText = useMemo(() => {
    if (!selectedFunnel) return '';

    return typeof tooltipContent === 'function'
      ? tooltipContent(selectedFunnel)
      : tooltipContent;
  }, [selectedFunnel, tooltipContent]);

  if (loading) {
    return (
      <ButtonSkeleton size={size} withIcon variant="secondary" fullWidth />
    );
  }

  const dropdownClasses = classes('w-full justify-between', className);
  const dropdownContainerClasses = classes(
    'w-full whitespace-nowrap text-ellipsis overflow-hidden',
    handlerContainerClassName
  );

  return (
    <Tooltip
      enabled={!isDropdownOpen}
      content={tooltipContentText}
      placement="bottom"
      size="md"
      className="w-[16rem]"
    >
      <MenuDropdown
        items={dropdownItems}
        icon={<PiFunnel className="w-4 h-4" />}
        mobileTitle={t('communications.dropdown.funnel.mobileTitle')}
        label={
          <Text
            size={getControlTextSize(size)}
            weight={getMenuItemContentWeight(false)}
            color={getMenuItemContentColor(false)}
            className={`${getMenuItemContentClasses()} whitespace-nowrap truncate`}
          >
            <span className="hidden xl:inline">
              {/* TODO: check when implementing i18n */}
              Embudo:{' '}
            </span>
            {selectedFunnelId ? selectedFunnel?.name : 'Selecciona un embudo'}
          </Text>
        }
        variant="secondary"
        appearance="outline"
        size={size}
        className={dropdownClasses}
        handlerContainerClassName={dropdownContainerClasses}
        placement="bottom-start"
        onOpenChange={setIsDropdownOpen}
      />
    </Tooltip>
  );
};
